import { useEffect, useState } from 'react'
import { getTopic } from '@gogrov/shared'
import { topicPhoto } from '@/lib/topicPhotos'
import type { PicStory } from '@/lib/picMatch'
import { useStoryPhoto } from '@/hooks/useStoryPhoto'

/**
 * The picture on every card (Brian v6.2, v6.8: "make sure all photos appear on
 * the feed"). The outlet's own photo first, credited to the outlet; with no
 * outlet photo, the library photo matched to the story (pics.js, Oct 29: pass
 * `story`); then the topic's stock photo (never credited to the outlet); if
 * that fails too, a drawn scene in the topic's colors, which always draws. Never posted,
 * exported or offered in Record: it is for the creator's eye only.
 */

// A color per topic (Brian's short ids), spread round the wheel.
const HUES: Record<string, [number, number]> = {
  realestate: [28, 36], development: [210, 22], newbusiness: [262, 34], outdoor: [140, 30], events: [330, 44], civic: [200, 14],
  food: [10, 46], celebritynews: [300, 34], safety: [0, 34], weather: [205, 30], sports: [120, 26], prosports: [95, 30],
  health: [170, 28], schools: [45, 44], tourism: [185, 36], finance: [150, 20], agriculture: [75, 34], artistsbands: [280, 34],
  arts: [320, 32], aviation: [215, 34], cars: [230, 16], fitness: [160, 34], geopolitics: [225, 12], history: [35, 22],
  homegarden: [100, 32], jobs: [25, 28], military: [80, 12], nonprofits: [345, 30], politics: [235, 20], realitytv: [290, 40],
  roads: [40, 30], space: [250, 40], technology: [195, 40], truecrime: [260, 12],
}

const scenes = new Map<string, string>()

/** A horizon, a warm light and a few blocks, in the topic's colors. */
export function drawnScene(topic: string | null | undefined, seedKey: string): string {
  const key = `${topic}:${seedKey}`
  const hit = scenes.get(key)
  if (hit) return hit
  if (typeof document === 'undefined') return ''
  const c = document.createElement('canvas'); c.width = 640; c.height = 800
  const g = c.getContext('2d'); if (!g) return ''
  const [hue, sat] = HUES[getTopic(topic)?.icon ?? ''] ?? [30, 20]
  let seed = 7; for (const ch of seedKey) seed = (seed * 31 + ch.charCodeAt(0)) % 997
  const sky = g.createLinearGradient(0, 0, 640, 800)
  sky.addColorStop(0, `hsl(${hue} ${sat}% 36%)`); sky.addColorStop(1, `hsl(${hue + 30} ${sat}% 13%)`)
  g.fillStyle = sky; g.fillRect(0, 0, 640, 800)
  g.fillStyle = `hsla(${hue + 20} 70% 70% / .5)`
  g.beginPath(); g.arc(470 - (seed % 180), 190 + (seed % 70), 46 + (seed % 30), 0, Math.PI * 2); g.fill()
  g.fillStyle = `hsl(${hue} ${sat}% 12%)`
  g.beginPath(); g.moveTo(0, 800); g.lineTo(0, 470)
  for (let x = 0; x <= 640; x += 40) g.lineTo(x, 430 + ((seed * (x + 11)) % 110))
  g.lineTo(640, 800); g.fill()
  g.fillStyle = 'rgba(0,0,0,.26)'
  for (let i = 0; i < 5; i++) { const w = 54 + ((seed * (i + 3)) % 90); g.fillRect((i * 140 + seed) % 600, 560 - ((seed * (i + 1)) % 90), w, 260) }
  const url = c.toDataURL('image/jpeg', 0.8)
  scenes.set(key, url)
  return url
}

export function FeedPhoto({ url, credit, topic, seedKey, story, className, alt = '' }: {
  url: string | null | undefined
  /** The story, for matching a library photo when there is no outlet photo. */
  story?: PicStory | null
  credit?: string | null
  topic: string | null | undefined
  seedKey: string
  className?: string
  alt?: string
}) {
  const lib = useStoryPhoto(url ? null : story)
  const chain = [url || lib, topicPhoto(topic)].filter(Boolean) as string[]
  const [step, setStep] = useState(0)
  useEffect(() => { setStep(0) }, [url, lib, topic])
  const src = step < chain.length ? chain[step] : drawnScene(topic, seedKey)
  const isOutlet = !!url && step === 0
  return (
    <div className={`feed-ph${className ? ` ${className}` : ''}`}>
      <img src={src} alt={alt} draggable={false} loading="lazy" onError={() => setStep((s) => s + 1)} />
      {isOutlet && credit && <span className="feed-ph__credit">{credit}</span>}
    </div>
  )
}
